import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from "recharts";
import { MapPin, Users, VenetianMask, Cake, Monitor, TrendingUp, Filter } from "lucide-react";

const COLORS = ["#FF4747", "#0038FF", "#FFD500", "#00D084", "#FFB4E1", "#B8E0FF", "#B6F3D6", "#9333EA"];
const FUNNEL_COLORS = ["#0038FF", "#FF4747", "#FFD500", "#00D084", "#9333EA"];

function StatTile({ icon, label, value, color = "bg-nh-mint" }) {
  return (
    <div className="nh-card p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`h-10 w-10 rounded-lg border-2 border-black ${color} flex items-center justify-center`}>{icon}</div>
      <div className="text-xs uppercase font-bold text-[var(--nh-muted)] mt-3">{label}</div>
      <div className="text-3xl font-display font-black">{value}</div>
    </div>
  );
}

function ChartCard({ title, icon, children, testid }) {
  return (
    <div className="nh-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-lg border-2 border-black bg-nh-accent flex items-center justify-center">{icon}</div>
        <h3 className="font-display font-black text-lg">{title}</h3>
      </div>
      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>{children}</ResponsiveContainer>
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics")
      .then((r) => { setData(r.data); setLoading(false); })
      .catch((e) => { setError(e?.message || "Failed to load"); setLoading(false); });
  }, []);

  if (loading) return <div className="mt-8 text-center py-12"><div className="inline-block h-8 w-8 border-4 border-black border-t-transparent rounded-full animate-spin"/></div>;
  if (error) return <div className="mt-8 nh-card p-6"><p className="text-red-600 font-bold">{error}</p></div>;
  if (!data) return null;

  const t = data.totals;
  return (
    <div className="mt-6 space-y-6" data-testid="admin-analytics">
      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatTile icon={<Users size={18}/>} label="Total Users" value={t.users} color="bg-nh-mint"/>
        <StatTile icon={<Users size={18}/>} label="Youth" value={t.youth} color="bg-nh-sky"/>
        <StatTile icon={<Users size={18}/>} label="Employers" value={t.employers} color="bg-nh-accent"/>
        <StatTile icon={<TrendingUp size={18}/>} label="Jobs" value={t.jobs} color="bg-nh-pink"/>
        <StatTile icon={<TrendingUp size={18}/>} label="Applications" value={t.applications} color="bg-white"/>
        <StatTile icon={<TrendingUp size={18}/>} label="Hired" value={t.hired} color="bg-nh-mint"/>
      </div>

      {/* Signups over time */}
      <ChartCard title="Signups — last 30 days" icon={<TrendingUp size={16}/>} testid="chart-signups">
        <LineChart data={data.signups_30d}>
          <CartesianGrid strokeDasharray="3 3" stroke="#00000014"/>
          <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d)=>d.slice(5)}/>
          <YAxis tick={{ fontSize: 10 }} allowDecimals={false}/>
          <Tooltip/>
          <Line type="monotone" dataKey="count" stroke="#FF4747" strokeWidth={3} dot={{ fill: "#0A0A0A", r: 3 }}/>
        </LineChart>
      </ChartCard>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Location — by state */}
        <ChartCard title="Users by State (Top 20)" icon={<MapPin size={16}/>} testid="chart-state">
          {data.by_state?.length ? (
            <BarChart data={data.by_state}>
              <CartesianGrid strokeDasharray="3 3" stroke="#00000014"/>
              <XAxis dataKey="name" tick={{ fontSize: 10 }}/>
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false}/>
              <Tooltip/>
              <Bar dataKey="count" fill="#0038FF" stroke="#0A0A0A" strokeWidth={2}/>
            </BarChart>
          ) : <EmptyChart label="No state data yet — users add state in profile"/>}
        </ChartCard>

        {/* Age */}
        <ChartCard title="Users by Age Bracket" icon={<Cake size={16}/>} testid="chart-age">
          <BarChart data={data.by_age_bracket}>
            <CartesianGrid strokeDasharray="3 3" stroke="#00000014"/>
            <XAxis dataKey="name" tick={{ fontSize: 11 }}/>
            <YAxis tick={{ fontSize: 10 }} allowDecimals={false}/>
            <Tooltip/>
            <Bar dataKey="count" fill="#FFD500" stroke="#0A0A0A" strokeWidth={2}/>
          </BarChart>
        </ChartCard>

        {/* Sex */}
        <ChartCard title="Users by Sex" icon={<VenetianMask size={16}/>} testid="chart-gender">
          <PieChart>
            <Pie data={data.by_gender} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                 stroke="#0A0A0A" strokeWidth={2} label={(e)=>`${e.name}: ${e.count}`}>
              {data.by_gender.map((_, i)=><Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
            </Pie>
            <Tooltip/>
          </PieChart>
        </ChartCard>

        {/* Browser */}
        <ChartCard title="Users by Browser" icon={<Monitor size={16}/>} testid="chart-browser">
          <PieChart>
            <Pie data={data.by_browser} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                 stroke="#0A0A0A" strokeWidth={2} label={(e)=>`${e.name}: ${e.count}`}>
              {data.by_browser.map((_, i)=><Cell key={i} fill={COLORS[(i + 3) % COLORS.length]}/>)}
            </Pie>
            <Tooltip/>
          </PieChart>
        </ChartCard>
      </div>

      {/* Conversion Funnel */}
      <div className="nh-card p-5" data-testid="chart-funnel">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-8 w-8 rounded-lg border-2 border-black bg-nh-primary text-white flex items-center justify-center"><Filter size={16}/></div>
          <div>
            <h3 className="font-display font-black text-lg">Conversion Funnel — Youth</h3>
            <p className="text-xs text-[var(--nh-muted)]">Where teens drop off in their journey. Focus UX effort on the biggest step gaps.</p>
          </div>
        </div>
        <div className="space-y-3">
          {(data.funnel || []).map((f, i) => {
            const top = (data.funnel && data.funnel[0]?.count) || 1;
            const widthPct = Math.max(6, Math.round((f.count / top) * 100));
            return (
              <div key={f.step} className="flex items-center gap-3" data-testid={`funnel-step-${f.step.toLowerCase().replace(/\s/g,'-')}`}>
                <div className="w-40 shrink-0 text-sm font-bold uppercase tracking-wide">{f.step}</div>
                <div className="flex-1 relative h-10 border-2 border-black rounded-md bg-nh-bg overflow-hidden">
                  <div
                    className="h-full border-r-2 border-black flex items-center px-3 text-white font-black text-sm"
                    style={{ width: `${widthPct}%`, background: FUNNEL_COLORS[i % FUNNEL_COLORS.length] }}
                  >
                    {f.count}
                  </div>
                </div>
                <div className="w-56 shrink-0 text-xs text-[var(--nh-muted)]">
                  {i === 0 ? (
                    <span className="font-bold">Baseline (100%)</span>
                  ) : (
                    <>
                      <span className="font-bold">{f.rate_from_prev}%</span> from previous ·{" "}
                      <span>{f.rate_from_top}% of signups</span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-[var(--nh-muted)] mt-4">
          Profile-complete = age set + at least one skill + one interest + state chosen.
        </p>
      </div>

      <ChartCard title="Users by Role" icon={<Users size={16}/>} testid="chart-role">
        <PieChart>
          <Pie data={data.by_role} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80}
               stroke="#0A0A0A" strokeWidth={2} label={(e)=>`${e.name}: ${e.count}`}>
            {data.by_role.map((_, i)=><Cell key={i} fill={COLORS[(i + 2) % COLORS.length]}/>)}
          </Pie>
          <Legend/>
          <Tooltip/>
        </PieChart>
      </ChartCard>
    </div>
  );
}

function EmptyChart({ label }) {
  return (
    <div className="h-full flex items-center justify-center text-sm text-[var(--nh-muted)] text-center px-6">
      {label}
    </div>
  );
}
